import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { SectionLabel } from "./SectionLabel";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const weightData = [
  { d: "Mon", w: 96.0 },
  { d: "Tue", w: 95.8 },
  { d: "Wed", w: 95.6 },
  { d: "Thu", w: 95.4 },
  { d: "Fri", w: 95.1 },
  { d: "Sat", w: 94.9 },
  { d: "Sun", w: 94.6 },
];

const loggedDays = [true, true, true, false, true, true, true];

export const EOWReport = () => (
  <section className="py-24 md:py-32 lg:py-40" data-testid="eow-report-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionLabel number={5} title="End of Week" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6"
        >
          <div className="relative mx-auto max-w-[420px]">
            <div
              aria-hidden="true"
              className="absolute -inset-x-8 -inset-y-10 -z-10 blur-3xl opacity-[0.08] pointer-events-none"
              style={{ background: "radial-gradient(closest-side, #176B4D, transparent 70%)" }}
            />
            <div
              className="rounded-[1.75rem] bg-[#0B0C0E] text-[#F8F7F3] p-6 md:p-7 border border-white/5 shadow-[0_30px_80px_-20px_rgba(23,23,23,0.18)]"
              data-testid="eow-report-card"
            >
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-white/40">
                This week
              </p>

              <div className="mt-5 h-28">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weightData}>
                    <YAxis hide domain={["dataMin - 0.5", "dataMax + 0.5"]} />
                    <Line
                      type="monotone"
                      dataKey="w"
                      stroke="#B8D99A"
                      strokeWidth={2.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between text-[11px] text-white/35 -mt-2 mb-5">
                <span>Mon</span>
                <span>Weight trend</span>
                <span>Sun</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/8 pt-5">
                <span className="text-sm text-white/50">Days logged</span>
                <div className="flex gap-1.5">
                  {loggedDays.map((on, i) => (
                    <span
                      key={i}
                      className={`h-2 w-2 rounded-full ${on ? "bg-[#B8D99A]" : "bg-white/15"}`}
                    />
                  ))}
                </div>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-white/60 border-t border-white/8 pt-5">
                Weekday intake ran consistently higher than weekends — most
                of the gap came from dinner.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-6">
          <motion.h2
            {...fadeUp}
            className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight font-medium text-[#171717]"
          >
            See the patterns you can&rsquo;t see day-to-day.
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-6 text-lg md:text-xl leading-relaxed text-[#171717]/70 max-w-[440px]"
          >
            Quantify looks across the week to help you understand weight
            trends, eating behaviour, consistency and what&rsquo;s actually
            changing.
          </motion.p>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.18 }}
            className="mt-6 text-base text-[#171717]/50 max-w-[440px]"
          >
            Here&rsquo;s what your week tells you — not just what it measured.
          </motion.p>
        </div>
      </div>
    </div>
  </section>
);
